/**
 * @file Lit l'état d'accès d'un abonnement (paiement en échec, module suspendu, essai qui se termine).
 * @module packages.common.utils.subscriptionAccessHelpers
 *
 * L'accès lui-même est décidé côté serveur (`isActive`, `accessGrace`) ; ces helpers
 * ne font que le lire pour l'affichage. Partagés par la page Modules des réglages,
 * le panel admin et la bannière globale du dashboard.
 */

/**
 * Statuts Stripe d'un abonnement dont le paiement n'a pas abouti (grâce en cours ou module suspendu).
 */
export const PAYMENT_ISSUE_STATUSES = ['past_due', 'unpaid'];

/**
 * Nombre de jours avant la fin d'un essai offert à partir duquel on prévient l'établissement.
 */
export const TRIAL_ENDING_NOTICE_DAYS = 7;

/**
 * Types de message affichés par la bannière globale, du plus urgent au moins urgent.
 */
export const SUBSCRIPTION_NOTICE_KINDS = Object.freeze({
  SUSPENDED: 'suspended',
  GRACE: 'grace',
  TRIAL_EXPIRED: 'trial_expired',
  TRIAL_ENDING: 'trial_ending',
});

/**
 * Calcule le nombre de jours restants avant une date (arrondi au jour supérieur).
 *
 * @param {string|Date} endDate - Date de fin
 * @param {Date} [now=new Date()] - Instant de référence
 * @return {number|null} Nombre de jours restants, null sans date
 */
export const getDaysRemaining = (endDate, now = new Date()) => {
  if (!endDate) return null;
  const diffMs = new Date(endDate) - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Indique si l'abonnement a un paiement en échec à régulariser.
 *
 * @param {Object} subscription - Abonnement
 * @return {boolean} Vrai si le paiement est en échec
 */
export const hasPaymentIssue = (subscription) => PAYMENT_ISSUE_STATUSES.includes(subscription?.status);

/**
 * Indique si le module est suspendu faute de paiement : paiement en échec et
 * accès fermé par le serveur (fin de la période de grâce ou abandon des relances).
 *
 * @param {Object} subscription - Abonnement
 * @return {boolean} Vrai si le module est suspendu
 */
export const isSuspendedForPayment = (subscription) => hasPaymentIssue(subscription) && subscription?.isActive === false;

/**
 * Indique si l'abonnement est en période de grâce : paiement en échec mais accès encore ouvert.
 *
 * @param {Object} subscription - Abonnement
 * @return {boolean} Vrai si la grâce est en cours
 */
export const isInPaymentGrace = (subscription) => hasPaymentIssue(subscription) && subscription?.isActive !== false;

const isTrialGrant = (subscription) => Boolean(subscription?.isManualGrant) && subscription?.grantType === 'trial';

/**
 * Détermine le message à afficher en tête du dashboard, ou null si tout va bien.
 *
 * Priorité : module suspendu, puis grâce en cours, puis essai offert terminé,
 * puis essai offert qui se termine sans carte enregistrée. Un seul message à la
 * fois, sur l'abonnement le plus urgent ; les autres restent visibles dans Réglages.
 *
 * @param {Object} params - Données lues par les hooks
 * @param {Array<Object>} params.subscriptions - Abonnements de l'établissement (bruts, avec `isActive`)
 * @param {boolean} params.hasPaymentMethod - Vrai si une carte est enregistrée
 * @param {Date} [params.now=new Date()] - Instant de référence
 * @return {{kind: string, subscription: Object}|null} Message à afficher ou null
 */
export const resolveSubscriptionNotice = ({subscriptions, hasPaymentMethod, now = new Date()}) => {
  const list = Array.isArray(subscriptions) ? subscriptions : [];

  const suspended = list.find(isSuspendedForPayment);
  if (suspended) return {kind: SUBSCRIPTION_NOTICE_KINDS.SUSPENDED, subscription: suspended};

  const inGrace = list.find(isInPaymentGrace);
  if (inGrace) return {kind: SUBSCRIPTION_NOTICE_KINDS.GRACE, subscription: inGrace};

  const trials = list.filter((sub) => isTrialGrant(sub) && sub.status === 'active' && sub.currentPeriodEnd);

  const expired = trials.find((sub) => getDaysRemaining(sub.currentPeriodEnd, now) <= 0);
  if (expired) return {kind: SUBSCRIPTION_NOTICE_KINDS.TRIAL_EXPIRED, subscription: expired};

  if (hasPaymentMethod) return null;
  const ending = trials.find((sub) => getDaysRemaining(sub.currentPeriodEnd, now) <= TRIAL_ENDING_NOTICE_DAYS);
  if (ending) return {kind: SUBSCRIPTION_NOTICE_KINDS.TRIAL_ENDING, subscription: ending};

  return null;
};
